import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Slider from './Slider/Slider'
import { FiExternalLink, FiArrowLeft, FiCheck } from 'react-icons/fi'
import { DiGithubBadge } from 'react-icons/di'
import SingleProjectSkeleton from './SingleProjectSkeleton'

const BackBtn = () => {
  const navigate = useNavigate()
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={() => navigate(-1)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 18px',
        marginBottom: '24px',
        borderRadius: '10px',
        border: `1px solid rgba(250,204,21,${hovered ? '0.4' : '0.14'})`,
        background: hovered ? 'rgba(250,204,21,0.07)' : 'rgba(255,255,255,0.03)',
        color: hovered ? '#facc15' : '#6b7280',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        fontFamily: 'Space Grotesk, sans-serif',
        boxShadow: hovered ? '0 0 14px rgba(250,204,21,0.14)' : 'none',
        transform: hovered ? 'translateX(-4px)' : 'translateX(0)',
        transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        cursor: 'pointer',
      }}
    >
      <FiArrowLeft size={13} />
      Back to Portfolio
    </button>
  )
}

const TechTag = ({ label }) => (
  <span style={{
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    background: 'rgba(250,204,21,0.08)',
    border: '1px solid rgba(250,204,21,0.22)',
    color: 'rgba(250,204,21,0.85)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
    boxShadow: '0 0 8px rgba(250,204,21,0.08)',
  }}>
    {label.trim()}
  </span>
)

const LinkBtn = ({ href, icon: Icon, label, color, rgb }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '9px 18px',
        borderRadius: '10px',
        border: `1px solid rgba(${rgb},${hovered ? '0.55' : '0.2'})`,
        background: hovered ? `rgba(${rgb},0.1)` : `rgba(${rgb},0.04)`,
        color: hovered ? color : `rgba(${rgb},0.6)`,
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        textDecoration: 'none',
        fontFamily: 'Space Grotesk, sans-serif',
        boxShadow: hovered ? `0 0 16px rgba(${rgb},0.2)` : 'none',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <Icon size={15} />
      {label}
    </a>
  )
}

const SectionHeading = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
    <h3 style={{
      color: '#facc15',
      fontSize: '1rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      textShadow: '0 0 16px rgba(250,204,21,0.4)',
      margin: 0,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </h3>
    <div style={{
      flex: 1, height: '1px',
      background: 'linear-gradient(90deg, rgba(250,204,21,0.3), transparent)',
    }} />
  </div>
)

const SingleProject = () => {
  const { id } = useParams()
  const [project, setProject] = useState(null)
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`https://portfolio-server-ten-theta.vercel.app/project/${id}`)
      .then((res) => res.json())
      .then((data) => { setProject(data); setLoading(false) })
  }, [id])

  if (isLoading) return <SingleProjectSkeleton />

  const { gallery, title, subTitle, details, live_link, technologics, client_link, features, server_link } = project

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

      <BackBtn />

      {/* title */}
      <div style={{
        padding: '20px 24px',
        borderRadius: '14px',
        background: 'linear-gradient(145deg, rgba(250,204,21,0.06), rgba(8,8,14,0.9))',
        border: '1px solid rgba(250,204,21,0.12)',
        boxShadow: '0 0 20px rgba(250,204,21,0.06), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}>
        <h1 style={{
          color: '#facc15',
          fontSize: 'clamp(1.4rem, 4vw, 2rem)',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          textShadow: '0 0 24px rgba(250,204,21,0.4)',
          margin: '0 0 6px',
          lineHeight: 1.2,
        }}>
          {title}
        </h1>
        <p style={{ color: '#9ca3af', fontSize: '0.95rem', margin: 0 }}>
          {subTitle}
        </p>
      </div>

      {/* slider */}
      <div style={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <Slider gallery={gallery} />
      </div>

      {/* tech + links row */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '18px 20px',
        borderRadius: '14px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {technologics?.split(',').map((t, i) => <TechTag key={i} label={t} />)}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {live_link && (
            <LinkBtn href={live_link} icon={FiExternalLink} label='Live Preview'
              color='#facc15' rgb='250,204,21' />
          )}
          {client_link && (
            <LinkBtn href={client_link} icon={DiGithubBadge}
              label={server_link ? 'Client Code' : 'GitHub'}
              color='#e2e8f0' rgb='226,232,240' />
          )}
          {server_link && (
            <LinkBtn href={server_link} icon={DiGithubBadge} label='Server Code'
              color='#818cf8' rgb='129,140,248' />
          )}
        </div>
      </div>

      {/* description */}
      <div style={{
        padding: '20px 24px',
        borderRadius: '14px',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <SectionHeading>Description</SectionHeading>
        <p style={{ color: '#9ca3af', lineHeight: 1.8, fontSize: '14px', margin: 0 }}>
          {details}
        </p>
      </div>

      {/* features */}
      {features?.length > 0 && (
        <div style={{
          padding: '20px 24px',
          borderRadius: '14px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <SectionHeading>Features</SectionHeading>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {features.map((feature, i) => (
              <li key={i} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                color: '#9ca3af',
                fontSize: '14px',
                lineHeight: 1.6,
              }}>
                <span style={{
                  marginTop: '3px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'rgba(52,211,153,0.1)',
                  border: '1px solid rgba(52,211,153,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <FiCheck size={10} style={{ color: '#34d399' }} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  )
}

export default SingleProject
